// File: src/pages/PortalPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { getPortalAccess, takePortalAccessHandoff, clearPortalAccessHandoff } from '../utils/portalAccess';
import { IoChevronForward, IoLogOutOutline, IoPeopleOutline } from 'react-icons/io5';
import { LuCrown } from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

export default function PortalPage() {
  const token = localStorage.getItem('accessToken');

  const [loading, setLoading] = useState(true);
  const [hasHostAccess, setHasHostAccess] = useState(false);
  const [hasReferralPartnerAccess, setHasReferralPartnerAccess] = useState(false);
  const [error, setError] = useState('');
  const loginHandoff = useRef(null);

  useEffect(() => {
    let active = true;

    async function loadAccess() {
      setLoading(true);
      setError('');
      setHasHostAccess(false);
      setHasReferralPartnerAccess(false);
      try {
        if (loginHandoff.current?.token !== token) {
          loginHandoff.current = { token, access: takePortalAccessHandoff(token) };
        }
        const access = loginHandoff.current.access || await getPortalAccess(token);
        if (!active) return;
        if (!access.hasHostAccess && !access.hasReferralPartnerAccess) {
          setError('This account does not have access to a Tribe portal.');
          return;
        }
        setHasHostAccess(access.hasHostAccess);
        setHasReferralPartnerAccess(access.hasReferralPartnerAccess);
      } catch (err) {
        if (active) {
          setError(err.response?.status === 401
            ? 'Please sign in again to access your portals.'
            : 'Unable to load your portal access. Please try again.');
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    loadAccess();
    return () => { active = false; };
  }, [token]);

  function openHostPortal() {
    window.location.href = '/host';
  }

  function openReferralPartnerPortal() {
    window.location.href = '/partner';
  }

  function logout() {
    clearPortalAccessHandoff();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userId');

    window.location.replace('/events');
  }

  return (
    <main style={styles.page}>
      <style>{`
        .portal-logo :is(path,circle,rect,polygon,polyline,ellipse) {
          fill: #050505 !important;
        }

        .portal-logo [fill="none"] {
          fill: none !important;
        }

        .portal-logo * {
          stroke: none !important;
        }

        button:focus-visible {
          outline: 0;
          box-shadow: 0 0 0 3px rgba(59,130,246,.35);
        }
      `}</style>

      <div style={styles.container}>
        <div style={styles.logoWrap}>
          <TribeLogo
            className="portal-logo"
            style={styles.logo}
            aria-label="Tribe"
          />
        </div>

        {loading ? (
          <div style={styles.loadingWrap}>
            <div style={styles.spinner} />
            <div style={styles.loadingText}>Loading your portals...</div>
          </div>
        ) : (
          <>
            <div style={styles.heading}>
              <div style={styles.eyebrow}>TRIBE PORTAL</div>
              <h1 style={styles.title}>Choose your portal</h1>
              <p style={styles.subtitle}>Select the portal you want to access.</p>
            </div>

            {error ? (
              <div style={styles.error}>{error}</div>
            ) : (
              <div style={styles.portalList}>
                {hasHostAccess && (
                  <button
                    type="button"
                    style={styles.portalButton}
                    onClick={openHostPortal}
                  >
                    <div style={styles.iconWrap}>
                      <LuCrown size={20} strokeWidth={2.1} />
                    </div>

                    <div style={styles.portalCopy}>
                      <div style={styles.portalTitle}>Host Marketplace</div>
                      <div style={styles.portalSubtitle}>
                        Coming soon: browse events and sign up to host.
                      </div>
                    </div>

                    <IoChevronForward style={styles.chevron} />
                  </button>
                )}

                {hasReferralPartnerAccess && (
                  <button
                    type="button"
                    style={styles.portalButton}
                    onClick={openReferralPartnerPortal}
                  >
                    <div style={styles.iconWrap}>
                      <IoPeopleOutline size={21} />
                    </div>

                    <div style={styles.portalCopy}>
                      <div style={styles.portalTitle}>Referral Dashboard</div>
                      <div style={styles.portalSubtitle}>
                        View referrals, earnings, and payments
                      </div>
                    </div>

                    <IoChevronForward style={styles.chevron} />
                  </button>
                )}
              </div>
            )}

            <button type="button" style={styles.logoutButton} onClick={logout}>
              <IoLogOutOutline size={17} />
              Log out
            </button>
          </>
        )}
      </div>
    </main>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#fff',
    color: '#0b0b0b',
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    display: 'flex',
    justifyContent: 'center',
  },

  container: {
    width: '100%',
    maxWidth: 520,
    padding: '28px 20px 60px',
    boxSizing: 'border-box',
  },

  logoWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 60,
  },

  logo: {
    width: 72,
    height: 'auto',
    display: 'block',
  },

  heading: {
    marginBottom: 24,
  },

  eyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.1em',
    color: '#8a8a8a',
    marginBottom: 9,
  },

  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.05,
    fontWeight: 850,
    letterSpacing: '-0.04em',
  },

  subtitle: {
    margin: '9px 0 0',
    fontSize: 13,
    lineHeight: 1.45,
    color: '#737373',
  },

  portalList: {
    display: 'grid',
    gap: 10,
  },

  portalButton: {
    width: '100%',
    minHeight: 78,
    border: '1px solid #e8e8e8',
    borderRadius: 14,
    background: '#fff',
    padding: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: 13,
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  iconWrap: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: '#f4f4f4',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  },

  portalCopy: {
    flex: 1,
    minWidth: 0,
  },

  portalTitle: {
    fontSize: 14,
    fontWeight: 850,
    color: '#111',
  },

  portalSubtitle: {
    marginTop: 4,
    fontSize: 11.5,
    lineHeight: 1.4,
    color: '#777',
  },

  chevron: {
    fontSize: 18,
    color: '#999',
    flexShrink: 0,
  },

  logoutButton: {
    marginTop: 28,
    border: 'none',
    background: 'transparent',
    color: '#666',
    padding: '8px 0',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12,
    fontWeight: 750,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  error: {
    padding: 16,
    borderRadius: 12,
    background: '#f7f7f7',
    fontSize: 12,
    lineHeight: 1.5,
    color: '#555',
  },

  loadingWrap: {
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },

  spinner: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '2px solid #ededed',
    borderTopColor: '#111',
  },

  loadingText: {
    marginTop: 12,
    fontSize: 11,
    color: '#777',
  },
};
